import { useState, useEffect, useCallback } from "react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";

/**
 * useFunctions — the Job Functions registry (SOUND-504).
 *
 * GET /functions is internal-only, so pass `enabled: false` on non-internal
 * surfaces to skip the call (avoids a guaranteed 403). `include: "holders"`
 * enriches each function with `holders[]`, `holderCount`, and a resolved
 * `defaultOwner`; `includeInactive` returns inactive functions too (registry).
 *
 * useState/useEffect + makeApiRequest (house pattern), CompanyHeader from
 * useEffectiveCompanyId().
 */
export function useFunctions({ include, includeInactive = false, enabled = true } = {}) {
  const [functions, setFunctions] = useState([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  const auth = useAuth();
  const companyId = useEffectiveCompanyId();

  const fetchFunctions = useCallback(async () => {
    if (!enabled || !companyId || !auth?.user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (include) params.include = include;
      if (includeInactive) params.includeInactive = "true";
      const res = await makeApiRequest(
        "get",
        apiRoutes.listFunctions(params),
        auth.user,
        companyId
      );
      setFunctions(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [enabled, companyId, auth?.user, include, includeInactive]);

  useEffect(() => {
    fetchFunctions();
  }, [fetchFunctions]);

  return { functions, loading, error, refetch: fetchFunctions };
}
